import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FileText, Zap, Download, Copy, CheckCircle } from 'lucide-react'

export default function WriteReportPage() {
  const location = useLocation()
  const auditResults = location.state?.auditResults
  const [reportContent, setReportContent] = useState('')
  const [reportTitle, setReportTitle] = useState('')
  const [reportIntro, setReportIntro] = useState('')
  const [reportRecommendations, setReportRecommendations] = useState('')
  const [useAIGeneration, setUseAIGeneration] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [reportGenerated, setReportGenerated] = useState(false)

  const generateAIReport = () => {
    setIsGenerating(true)

    // Simulate AI report generation
    setTimeout(() => {
      const aiTitle = `${auditResults?.auditType} - Professional Audit Report`
      const aiIntro = `This comprehensive audit report has been prepared following industry best practices and standards. The audit was conducted on ${auditResults?.timestamp} with audit ID ${auditResults?.auditId}. The overall result of the audit is: ${auditResults?.status}.`
      const aiRecommendations = `
Based on the findings, the following recommendations are made:

1. Critical Issues (${auditResults?.criticalIssues} identified):
   - Implement immediate corrective actions for all critical issues
   - Establish an emergency response protocol
   - Schedule follow-up audit within 7-14 days

2. High Risk Issues (${auditResults?.highRiskIssues} identified):
   - Develop action plan for remediation
   - Assign responsible parties and timelines
   - Monthly review of progress

3. Medium Risk Issues (${auditResults?.mediumRiskIssues} identified):
   - Include in quarterly review process
   - Monitor for any escalation
   - Plan long-term resolution

4. Best Practices Implementation:
   - Establish continuous monitoring procedures
   - Document all findings and resolutions
   - Implement regular audit schedule
   - Train staff on compliance requirements

5. Timeline for Compliance:
   - Critical: 7-14 days
   - High Risk: 30 days
   - Medium Risk: 60-90 days
      `

      setReportTitle(aiTitle)
      setReportIntro(aiIntro)
      setReportRecommendations(aiRecommendations)

      const fullReport = `
PROFESSIONAL AUDIT REPORT
========================

Title: ${aiTitle}

Executive Summary:
------------------
${aiIntro}

Audit Details:
- Audit Type: ${auditResults?.auditType}
- Audit ID: ${auditResults?.auditId}
- Audit Date: ${auditResults?.timestamp}
- Overall Status: ${auditResults?.status}

Risk Assessment:
- Critical Issues: ${auditResults?.criticalIssues}
- High Risk Issues: ${auditResults?.highRiskIssues}
- Medium Risk Issues: ${auditResults?.mediumRiskIssues}
- Total Findings: ${auditResults?.findings}

Findings Breakdown:
${auditResults?.details.map((d) => `- ${d.category}: ${d.status} - ${d.details}`).join('\n')}

Recommendations:
${aiRecommendations}

Conclusion:
-----------
This audit has identified areas requiring attention. By implementing the recommended actions, your organization can significantly improve its security and compliance posture. Continued monitoring and regular audits are essential to maintain compliance and address evolving threats.

Next Steps:
1. Review this report with all stakeholders
2. Prioritize critical issues for immediate action
3. Schedule follow-up audit to verify remediation
4. Implement preventive measures

Report Generated: ${new Date().toLocaleString()}
`

      setReportContent(fullReport)
      setReportGenerated(true)
      setIsGenerating(false)
    }, 2000)
  }

  const handleCustomizeReport = () => {
    const customReport = `
PROFESSIONAL AUDIT REPORT
========================

Title: ${reportTitle}

Executive Summary:
------------------
${reportIntro}

Audit Details:
- Audit Type: ${auditResults?.auditType}
- Audit ID: ${auditResults?.auditId}
- Audit Date: ${auditResults?.timestamp}
- Overall Status: ${auditResults?.status}

Risk Assessment:
- Critical Issues: ${auditResults?.criticalIssues}
- High Risk Issues: ${auditResults?.highRiskIssues}
- Medium Risk Issues: ${auditResults?.mediumRiskIssues}
- Total Findings: ${auditResults?.findings}

Findings Breakdown:
${auditResults?.details.map((d) => `- ${d.category}: ${d.status} - ${d.details}`).join('\n')}

Recommendations:
${reportRecommendations}

Conclusion:
-----------
This audit has identified areas requiring attention. By implementing the recommended actions, your organization can significantly improve its security and compliance posture.

Report Generated: ${new Date().toLocaleString()}
`

    setReportContent(customReport)
    setReportGenerated(true)
  }

  const downloadReport = () => {
    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(reportContent))
    element.setAttribute('download', `Audit-Report-${auditResults?.auditId}.txt`)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(reportContent)
    alert('Report copied to clipboard!')
  }

  if (!auditResults) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Write Report</h1>
          <p className="text-blue-200">No audit results found. Please run an audit first.</p>
        </div>
        <a
          href="/live-audit"
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all inline-flex items-center space-x-2"
        >
          <span>Go to Live Audit</span>
        </a>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Write Professional Report</h1>
        <p className="text-blue-200">Transform your audit results into a professional report</p>
      </div>

      {reportGenerated ? (
        <>
          {/* Generated Report */}
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
              <FileText size={24} />
              <span>Professional Report</span>
            </h2>

            <div className="bg-slate-700/30 rounded-lg p-6 border border-blue-500/20 mb-6 max-h-96 overflow-y-auto">
              <pre className="text-blue-100 font-mono text-sm whitespace-pre-wrap">{reportContent}</pre>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={downloadReport}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center justify-center space-x-2"
              >
                <Download size={20} />
                <span>Download Report</span>
              </button>
              <button
                onClick={copyToClipboard}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all flex items-center justify-center space-x-2"
              >
                <Copy size={20} />
                <span>Copy to Clipboard</span>
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Report Generation Options */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* AI Auto-Generate */}
            <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-lg border border-purple-500/30 p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-white">AI Auto-Generate Report</h3>
                  <p className="text-blue-200 mt-2">Let AI create a professional report based on audit findings</p>
                </div>
                <Zap size={32} className="text-purple-400" />
              </div>

              <ul className="space-y-2 mb-6 text-sm text-blue-200">
                <li className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-400" />
                  <span>Professional formatting</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-400" />
                  <span>Executive summary</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-400" />
                  <span>Risk assessment</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-400" />
                  <span>Recommendations</span>
                </li>
              </ul>

              <button
                onClick={generateAIReport}
                disabled={isGenerating}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50"
              >
                {isGenerating ? 'Generating...' : 'Generate with AI'}
              </button>
            </div>

            {/* Manual Report Writing */}
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-8">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
                <FileText size={24} />
                <span>Manual Report</span>
              </h3>
              <p className="text-blue-200 mb-6">Customize every aspect of your report</p>

              <div className="space-y-4">
                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Report Title</label>
                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    placeholder="Enter report title"
                    className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                  />
                </div>

                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Executive Summary</label>
                  <textarea
                    value={reportIntro}
                    onChange={(e) => setReportIntro(e.target.value)}
                    placeholder="Write your executive summary here..."
                    rows="3"
                    className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                  />
                </div>

                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Recommendations</label>
                  <textarea
                    value={reportRecommendations}
                    onChange={(e) => setReportRecommendations(e.target.value)}
                    placeholder="Add your recommendations here..."
                    rows="3"
                    className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                  />
                </div>

                <button
                  onClick={handleCustomizeReport}
                  disabled={!reportTitle || !reportIntro}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all disabled:opacity-50"
                >
                  Create Report
                </button>
              </div>
            </div>
          </div>

          {/* Audit Summary */}
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Audit Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-blue-300/70 text-sm mb-1">Audit Type</p>
                <p className="text-white font-semibold">{auditResults.auditType}</p>
              </div>
              <div>
                <p className="text-blue-300/70 text-sm mb-1">Status</p>
                <p className={`font-semibold ${auditResults.status === 'Passed' ? 'text-green-400' : 'text-red-400'}`}>
                  {auditResults.status}
                </p>
              </div>
              <div>
                <p className="text-blue-300/70 text-sm mb-1">Total Issues</p>
                <p className="text-white font-semibold">{auditResults.findings}</p>
              </div>
              <div>
                <p className="text-blue-300/70 text-sm mb-1">Critical</p>
                <p className="text-red-400 font-semibold">{auditResults.criticalIssues}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
